import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { Users, Car, FileText, Receipt, TrendingUp, Clock, Loader2, AlertTriangle, Calendar, CalendarDays } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ronFormat = (val) => (val || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await axios.get(API + '/admin/dashboard/stats', {
          headers: { Authorization: 'Bearer ' + token },
          withCredentials: true
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center py-12 text-slate-500">Nu s-au putut încărca statisticile</div>;
  }

  const currentMonth = new Date().toLocaleString('ro-RO', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-slate-500">Prezentare generala a activitatii</p>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white" data-testid="card-total-clients">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Clienti</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-clients-value">{stats.total_clients}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg"><Users className="h-6 w-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-total-vehicles">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Vehicule</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-vehicles-value">{stats.total_vehicles}</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg"><Car className="h-6 w-6 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-total-orders">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Comenzi</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-orders-value">{stats.total_orders}</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg"><FileText className="h-6 w-6 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-total-invoices">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Facturi</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-invoices-value">{stats.total_invoices}</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg"><Receipt className="h-6 w-6 text-purple-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white" data-testid="card-total-invoiced">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-slate-500">Total facturat</span>
            </div>
            <p className="text-2xl font-bold text-blue-600" data-testid="total-invoiced-value">{ronFormat(stats.total_invoiced_ron)} RON</p>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-total-collected">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <span className="text-sm text-slate-500">Total incasat</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600" data-testid="total-collected-value">{ronFormat(stats.total_collected_ron)} RON</p>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-total-pending">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-slate-500">Rest de incasat</span>
            </div>
            <p className="text-2xl font-bold text-amber-600" data-testid="total-pending-value">{ronFormat(stats.total_pending_ron)} RON</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-orange-200" data-testid="card-uninvoiced-orders">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <span className="text-sm text-slate-500">Comenzi nefacturate</span>
            </div>
            <p className="text-2xl font-bold text-orange-600" data-testid="uninvoiced-orders-value">{ronFormat(stats.uninvoiced_orders_ron)} RON</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly & Yearly */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white" data-testid="card-invoiced-month">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-indigo-600" />
              <span className="text-sm text-slate-500">Facturat luna curenta</span>
            </div>
            <p className="text-2xl font-bold text-indigo-600" data-testid="invoiced-month-value">{ronFormat(stats.invoiced_month_ron)} RON</p>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-collected-month">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-teal-600" />
              <span className="text-sm text-slate-500">Incasat luna curenta</span>
            </div>
            <p className="text-2xl font-bold text-teal-600" data-testid="collected-month-value">{ronFormat(stats.collected_month_ron)} RON</p>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-invoiced-year">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="h-4 w-4 text-violet-600" />
              <span className="text-sm text-slate-500">Facturat anul curent</span>
            </div>
            <p className="text-2xl font-bold text-violet-600" data-testid="invoiced-year-value">{ronFormat(stats.invoiced_year_ron)} RON</p>
          </CardContent>
        </Card>
        <Card className="bg-white" data-testid="card-collected-year">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="h-4 w-4 text-cyan-600" />
              <span className="text-sm text-slate-500">Incasat anul curent</span>
            </div>
            <p className="text-2xl font-bold text-cyan-600" data-testid="collected-year-value">{ronFormat(stats.collected_year_ron)} RON</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Comenzi recente</span>
              <Link to="/admin/orders" className="text-sm text-blue-600 hover:underline">Vezi toate</Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_orders.length === 0 ? (
              <p className="text-slate-500 text-sm">Nicio comandă</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_orders.map((order) => (
                  <Link key={order.order_id} to={`/admin/orders/${order.order_id}`} className="block p-3 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{order.order_number}</p>
                        <p className="text-sm text-slate-500">{order.client?.name || 'N/A'}</p>
                      </div>
                      <Badge variant="secondary">{order.status}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Facturi recente</span>
              <Link to="/admin/invoices" className="text-sm text-blue-600 hover:underline">Vezi toate</Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_invoices.length === 0 ? (
              <p className="text-slate-500 text-sm">Nicio factură</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_invoices.map((inv) => (
                  <Link key={inv.invoice_id} to={`/admin/invoices/${inv.invoice_id}`} className="block p-3 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{inv.invoice_number}</p>
                        <p className="text-sm text-slate-500">{inv.client?.name || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-slate-900">{(inv.total_ron || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2 })} RON</p>
                        <Badge variant={inv.payment_status === 'paid' ? 'default' : 'secondary'}>{inv.payment_status || 'unpaid'}</Badge>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
